import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getPublicMp2Catalog } from "./public/meal-plans-v2.functions";
import { getPublicUpcomingMenu } from "./public/meal-plans-v2-menu.functions";
import { listPublishedJobs, getJobBySlug } from "./careers.functions";
import { 
  cartGet, 
  cartAddItem, 
  cartRemoveItem, 
  cartUpdateItem, 
  listAlacarte,
  cartAddBeverage,
  cartCreate
} from "./public/mp2-alacarte-cart.functions";
import { 
  listConsultationTypes, 
  listConsultants, 
  getConsultantAvailability, 
  bookAppointment 
} from "./customer/appointments.functions";
import { submitCartOrder } from "./checkout/checkout.functions";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { getRequestHeader } from "@tanstack/react-start/server";
import { todayInIST } from "./date/ist";

/** 
 * Meal Plans Tool
 * Returns a list of available meal plans with authoritative pricing and duration details.
 */
export const getVapiMealPlans = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const catalog = await getPublicMp2Catalog();
      if (!catalog || !catalog.plans) return { error: "No meal plans available." };

      return {
        plans: catalog.plans.map(p => {
          // Resolve pricing matrix for this plan
          const durations = catalog.durations
            .filter(d => p.allowed_duration_ids.includes(d.id))
            .map(d => ({
              label: d.label,
              days: d.total_service_days
            }));

          return {
            title: p.title,
            slug: p.slug,
            tagline: p.tagline,
            description: p.description,
            total_meals: p.total_meals,
            service_days: p.service_days,
            durations: durations,
            badges: p.badges?.map((b: any) => b.label) || [],
            is_popular: p.is_popular,
            is_recommended: p.is_recommended
          };
        })
      };
    } catch (err) {
      console.error("[vapi] getVapiMealPlans failed", err);
      return { error: "I couldn't verify the meal plans right now." };
    }
  });

/**
 * Careers Tool
 * Returns currently published job openings.
 */
export const getVapiJobs = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const jobs = await listPublishedJobs();
      return jobs.map(j => ({
        title: j.title,
        slug: j.slug,
        location: j.location,
        type: j.employment_type,
        short_description: j.short_description
      }));
    } catch (err) {
      console.error("[vapi] getVapiJobs failed", err);
      return { error: "Could not retrieve job listings." };
    }
  });

/**
 * Job Details Tool
 */
export const getVapiJobDetails = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ slug: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      return await getJobBySlug({ data: { slug: data.slug } });
    } catch (err) {
      console.error("[vapi] getVapiJobDetails failed", err);
      return { error: "Could not retrieve job details." };
    }
  });

/**
 * Delivery Areas Tool
 * Answer questions about delivery availability and neighborhoods covered.
 */
export const getVapiDeliveryAreas = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const catalog = await getPublicMp2Catalog();
      if (!catalog || !catalog.delivery_areas) return { error: "Delivery area information unavailable." };
      
      return {
        areas: catalog.delivery_areas.map(a => a.neighbourhood),
        city: "Mumbai", // Authoritative context for the current project
        message: "We deliver across various neighborhoods in Mumbai."
      };
    } catch (err) {
      console.error("[vapi] getVapiDeliveryAreas failed", err);
      return { error: "I couldn't verify delivery availability for that area." };
    }
  });

/**
 * Delivery & Cutoffs Tool
 * Answer questions about delivery/order cutoff times using authoritative rules.
 */
export const getVapiDeliveryCutoffs = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    type: z.enum(["alacarte", "meal_plan"]),
    meal_slot: z.string().optional() 
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const area = { id: "cd329f18-2b21-45d3-8776-8c52ff88a33c", name: "Worli" };
      const { todayInIST } = await import("./date/ist");
      const today = todayInIST();
      const timezone = "Asia/Kolkata";
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      if (data.type === "meal_plan") {
        // Authoritative Meal Plan Rule: 6:00 PM cutoff for next-day start
        return {
          type: "meal_plan",
          message: "For all Meal Plans, the cutoff for starting your plan tomorrow is 6:00 PM today. If you order after 6:00 PM, your plan will start on the day after tomorrow.",
          cutoff_time: "06:00 PM",
          rule: "Next-day start requires ordering before 6:00 PM.",
          timezone
        };
      }

      const cutoffRes = await supabaseAdmin.rpc("mp2_alacarte_cutoffs", { _area_id: area.id, _on_date: today });
      const rawCutoffs = (cutoffRes.data as any) || [];
      const normalized = Array.isArray(rawCutoffs) ? rawCutoffs : (rawCutoffs.cutoffs || []);

      const getDisplayTime = (c: any) => {
        const ist = formatTimeIST(c.cutoff_at_ist);
        const slotName = (c.slot || "").toLowerCase();
        if ((!ist || ist === "—" || ist === "\u2014") && slotName.includes("lunch")) return "06:00 AM";
        return ist;
      };

      if (data.meal_slot) {
        const slotMatch = normalized.find((c: any) => 
          c.slot.toLowerCase() === data.meal_slot!.toLowerCase() || 
          c.slot.toLowerCase().includes(data.meal_slot!.toLowerCase())
        );

        if (slotMatch || data.meal_slot?.toLowerCase().includes("lunch")) {
          const time = slotMatch ? getDisplayTime(slotMatch) : "06:00 AM";
          const open = slotMatch ? slotMatch.is_open : false;
          return {
            type: "alacarte",
            requested_meal: data.meal_slot,
            cutoff_time: time,
            has_passed: !open,
            message: open 
              ? `Today's ${data.meal_slot} A La Carte cutoff is ${time}.`
              : `Today's ${data.meal_slot} A La Carte cutoff was ${time}, and it has already passed for today.`,
            timezone
          };
        }
      }

      return {
        type: "alacarte",
        cutoffs: normalized.map((c: any) => ({ 
          slot: c.slot, 
          time: getDisplayTime(c), 
          is_open: c.is_open 
        })),
        message: "We have different A La Carte cutoff times for each meal slot. Which one are you interested in?",
        timezone
      };
    } catch (err) {
      console.error("[vapi] getVapiDeliveryCutoffs failed", err);
      return { error: "I couldn't verify the delivery cutoff times right now." };
    }
  });

export const getVapiMealPlanQuote = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({
    plan_slug: z.string().default('weight-management'),
    duration_days: z.number().int().default(30),
    combination_code: z.string().default('lunch_only'),
    preference_code: z.string().default('veg'),
    delivery_area_name: z.string().optional()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      console.log("[VAPI_PRICING_TRACE] Resolving for:", data);
      const catalog = await getPublicMp2Catalog();
      if (!catalog || !catalog.plans) return { error: "Catalog unavailable." };

      let plan = catalog.plans.find(p => p.slug === data.plan_slug);
      if (!plan && data.plan_slug === 'weight-loss') {
        plan = catalog.plans.find(p => p.slug === 'weight-management');
      }
      const duration = catalog.durations.find(d => d.total_service_days === data.duration_days);
      const combination = catalog.combinations.find(c => c.code === data.combination_code);
      const preference = catalog.food_preferences.find(p => p.code === data.preference_code);

      if (!plan || !duration || !combination || !preference) {
        return { 
          error: "invalid_configuration",
          message: "I couldn't find that specific meal plan configuration. Could you please clarify the plan, duration, or meal selection?" 
        };
      }
      console.log("[VAPI_PRICING_TRACE] Catalog resolution:", { plan: !!plan, duration: !!duration, combination: !!combination, preference: !!preference });

      // Surgical Delivery-Rule Resolution
      let resolvedAreaId: string | undefined;
      let resolvedRuleId: string | undefined;

      // 1. Determine plan's allowed rules that are actually in the catalog (active)
      const allowedActiveRules = catalog.delivery_rules.filter(r => 
        plan!.allowed_delivery_rule_ids.includes(r.id)
      );

      // 2. Resolve based on provided area name
      if (data.delivery_area_name) {
        const area = catalog.delivery_areas.find(a => 
          a.neighbourhood.toLowerCase().includes(data.delivery_area_name!.toLowerCase())
        );
        if (area) {
          // Find if this area is mapped to ANY of the plan's allowed active rules
          const mapping = catalog.rule_areas.find(ra => 
            ra.area_id === area.id && 
            allowedActiveRules.some(r => r.id === ra.rule_id)
          );
          if (mapping) {
            resolvedAreaId = area.id;
            resolvedRuleId = mapping.rule_id;
            console.log("[VAPI_PRICING_TRACE] Resolved via user input:", { area: area.neighbourhood, ruleId: resolvedRuleId });
          }
        }
      }

      // 3. Fallback: Find the FIRST valid compatible mapping for this specific plan
      if (!resolvedAreaId || !resolvedRuleId) {
        const validMapping = catalog.rule_areas.find(ra => 
          allowedActiveRules.some(r => r.id === ra.rule_id) &&
          catalog.delivery_areas.some(a => a.id === ra.area_id)
        );

        if (validMapping) {
          resolvedAreaId = validMapping.area_id;
          resolvedRuleId = validMapping.rule_id;
          console.log("[VAPI_PRICING_TRACE] Resolved via plan-compatible fallback:", { areaId: resolvedAreaId, ruleId: resolvedRuleId });
        }
      }

      // 4. Final check: If still no compatible pair, we must ask for location
      if (!resolvedAreaId || !resolvedRuleId) {
        console.log("[VAPI_PRICING_TRACE] No compatible delivery resolution possible without area or for this specific plan.");
        return {
          error: "area_required",
          message: "To give you the exact price, which Mumbai area or pincode will the meal be delivered to?"
        };
      }

      const deliveryRuleId = resolvedRuleId;
      const areaId = resolvedAreaId;

      const { createClient } = await import("@supabase/supabase-js");

      const supabase = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
        auth: { storage: undefined, persistSession: false, autoRefreshToken: false }
      });
      const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: "Asia/Kolkata", year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      
      console.log("[VAPI_PRICING_TRACE] Final RPC Params:", {
        _plan_id: plan.id,
        _combination_id: combination.id,
        _preference_code: preference.code,
        _duration_id: duration.id,
        _delivery_rule_id: deliveryRuleId,
        _delivery_area_id: areaId,
        _requested_start_date: istDate,
      });

      const { data: quoteRes, error: quoteErr } = await (supabase as any).rpc("mp2_quote_price", {
        _plan_id: plan.id,
        _combination_id: combination.id,
        _preference_code: preference.code,
        _duration_id: duration.id,
        _delivery_rule_id: deliveryRuleId,
        _delivery_area_id: areaId,
        _requested_start_date: istDate,
        _checkout_submission_id: crypto.randomUUID()
      });

      
      if (quoteErr) {
        console.error("[VAPI_PRICING_TRACE] RPC Error:", quoteErr);
        if (quoteErr.message === 'delivery_rule_unavailable' || quoteErr.message === 'delivery_area_not_in_rule') {
          return {
            error: "area_required",
            message: "To give you the exact price, which Mumbai area or pincode will the meal be delivered to?"
          };
        }
        throw quoteErr;
      }
      const res = quoteRes;

      
      console.log("[VAPI_PRICING_TRACE] RPC success response:", JSON.stringify(res, null, 2));

      const quote = res as any;
      if (!quote || !quote.pricing) return { error: "Pricing unavailable for this selection." };

      return {
        success: true,
        price_rupees: quote.pricing.total_paise / 100,
        currency: "INR",
        plan: plan.title,
        duration: duration.label,
        combination: combination.label,
        preference: preference.label,
        message: `The price for the ${plan.title} (${duration.label}, ${combination.label}, ${preference.label}) is ₹${(quote.pricing.total_paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}.`
      };
    } catch (err) {
      console.error("[vapi] getVapiMealPlanQuote failed", err);
      return { error: "I couldn't retrieve the exact price right now." };
    }
  });

export const getVapiDishNutrition = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ dish_id: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: dish } = await supabaseAdmin
        .from("mp2_dishes")
        .select("name, calories_kcal, protein_g, carbs_g, fat_g, nutrition_confirmed")
        .eq("id", data.dish_id)
        .single();

      if (!dish) return { error: "Dish not found." };
      if (!dish.nutrition_confirmed) return { message: `Nutrition information for ${dish.name} is not currently available.` };

      return {
        dish_name: dish.name,
        calories: dish.calories_kcal,
        protein_g: dish.protein_g,
        carbs_g: dish.carbs_g,
        fat_g: dish.fat_g,
        message: `${dish.name} has ${dish.calories_kcal} calories, ${dish.protein_g}g protein, ${dish.carbs_g}g carbohydrates, and ${dish.fat_g}g fat.`
      };
    } catch (err) {
      console.error("[vapi] getVapiDishNutrition failed", err);
      return { error: "I couldn't retrieve the nutrition info right now." };
    }
  });

function formatTimeIST(isoStr: string | null): string {
  if (!isoStr || isoStr === "—" || isoStr === "\u2014") return "—";
  try {
    const date = new Date(isoStr);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }).format(date);
  } catch (e) {
    return "—";
  }
}

/**
 * Health Consultation Info Tool
 */
export const getVapiConsultationInfo = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const types = await listConsultationTypes();
      return {
        message: "We offer professional health consultations with experts.",
        types: types.map(t => ({
          name: t.name,
          description: t.description,
          duration: t.duration_minutes,
          price: t.price_paise / 100
        }))
      };
    } catch (err) {
      console.error("[vapi] getVapiConsultationInfo failed", err);
      return { error: "Consultation information is currently unavailable." };
    }
  });

/**
 * Weekly Menu Tool
 */
export const getVapiWeeklyMenu = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    plan_slug: z.string(),
    preference: z.string().optional()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const menu = await getPublicUpcomingMenu({ 
        data: { 
          plan_slug: data.plan_slug, 
          preference: data.preference 
        } 
      });
      return menu;
    } catch (err) {
      console.error("[vapi] getVapiWeeklyMenu failed", err);
      return { error: "I couldn't retrieve the weekly menu for that plan." };
    }
  });

/**
 * Authentication Status Tool
 */
export const getVapiAuthStatus = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const authHeader = getRequestHeader("authorization");
      if (!authHeader) return { authenticated: false };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: { user } } = await supabaseAdmin.auth.getUser(authHeader.replace("Bearer ", ""));
      
      if (!user) return { authenticated: false };

      return {
        authenticated: true,
        user: {
          id: user.id,
          email: user.email,
          name: user.user_metadata?.full_name
        }
      };
    } catch (err) {
      return { authenticated: false };
    }
  });

/**
 * A La Carte Search Tool
 */
export const searchVapiAlacarteItems = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    q: z.string().optional(),
    meal_slot: z.enum(["breakfast", "lunch", "snacks", "dinner"]).optional(),
    is_veg: z.boolean().optional()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const area_id = "cd329f18-2b21-45d3-8776-8c52ff88a33c"; // Worli
      const on_date = todayInIST();
      
      const res = await listAlacarte({ 
        data: { 
          area_id, 
          on_date, 
          slot: data.meal_slot,
          prefs: data.is_veg ? ["veg"] : undefined
        } 
      });

      if ('error' in res) return { error: "Catalog currently unavailable." };
      
      let dishes = res.dishes;
      if (data.q) {
        const query = data.q.toLowerCase();
        dishes = dishes.filter(d => 
          d.name.toLowerCase().includes(query) || 
          d.description?.toLowerCase().includes(query)
        );
      }

      return {
        items: dishes.map(d => ({
          id: d.id,
          name: d.name,
          price: d.unit_price_paise ? d.unit_price_paise / 100 : null,
          available: true,
          meal_slot: d.meal_slot,
          description: d.description
        }))
      };
    } catch (err) {
      console.error("[vapi] searchVapiAlacarteItems failed", err);
      return { error: "I couldn't verify the item details right now." };
    }
  });

export const getVapiAlacarteItem = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ item_id: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      const area_id = "cd329f18-2b21-45d3-8776-8c52ff88a33c";
      const on_date = todayInIST();
      const res = await listAlacarte({ data: { area_id, on_date } });
      
      if ('error' in res) return { error: "Item information unavailable." };
      
      const item = res.dishes.find(d => d.id === data.item_id);
      if (!item) return { error: "Item not found or unavailable for today." };

      return {
        name: item.name,
        price: item.unit_price_paise ? item.unit_price_paise / 100 : null,
        description: item.description,
        is_veg: item.is_veg,
        allergens: item.allergens
      };
    } catch (err) {
      return { error: "Could not retrieve item details." };
    }
  });

/**
 * Cart Management Tools
 */
export const getVapiMyCart = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ token: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated) return { error: "auth_required", message: "Please sign in to view your cart." };

      const snapshot = await cartGet({ data: { token: data.token } });
      if ('error' in snapshot) return snapshot;

      return {
        items: snapshot.items.map(i => ({
          item_id: i.id,
          name: i.dish?.name || i.beverage?.name || i.soup?.name || i.salad?.name || "Item",
          quantity: i.quantity,
          unit_price: i.unit_price_paise / 100,
          line_total: i.line_total_paise / 100
        })),
        subtotal: snapshot.items.reduce((acc, i) => acc + i.line_total_paise, 0) / 100,
        total: snapshot.items.reduce((acc, i) => acc + i.line_total_paise, 0) / 100 // Simplified for voice
      };
    } catch (err) {
      return { error: "Could not retrieve your cart." };
    }
  });

export const addVapiToCart = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    token: z.string(),
    dish_id: z.string(),
    slot: z.enum(["breakfast", "lunch", "snacks", "dinner"]),
    quantity: z.number().int().min(1).max(10),
    client_request_id: z.string().uuid()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated) return { error: "auth_required", message: "Please sign in to add items to your cart." };

      // Re-verify availability and price server-side
      const area_id = "cd329f18-2b21-45d3-8776-8c52ff88a33c";
      const on_date = todayInIST();
      const catalog = await listAlacarte({ data: { area_id, on_date, slot: data.slot } });
      
      if ('error' in catalog) return { error: "Catalog unavailable for validation." };
      const item = catalog.dishes.find(d => d.id === data.dish_id);
      if (!item) return { error: "Item no longer available for this slot." };

      // Check cutoff
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const cutoffRes = await supabaseAdmin.rpc("mp2_alacarte_cutoffs", { _area_id: area_id, _on_date: on_date });
      const cutoffs = (cutoffRes.data as any) || [];
      const slotCutoff = cutoffs.find((c: any) => c.slot.toLowerCase() === data.slot.toLowerCase());
      if (slotCutoff && !slotCutoff.is_open) return { error: `Ordering for ${data.slot} has closed for today.` };

      const snapshot = await cartAddItem({ 
        data: { 
          token: data.token, 
          dish_id: data.dish_id, 
          slot: data.slot,
          quantity: data.quantity,
          client_request_id: data.client_request_id
        } 
      });

      if ('error' in snapshot) return snapshot;
      
      return {
        success: true,
        message: `Added ${data.quantity} ${item.name} to your cart.`,
        cart_total: snapshot.items.reduce((acc, i) => acc + i.line_total_paise, 0) / 100
      };
    } catch (err) {
      console.error("[vapi] addVapiToCart failed", err);
      return { error: "Could not add item to cart." };
    }
  });

export const updateVapiCartItem = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    token: z.string(),
    item_id: z.string().uuid(),
    quantity: z.number().int().min(1).max(10),
    client_request_id: z.string().uuid()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated) return { error: "auth_required" };

      const res = await cartUpdateItem({ data });
      if ('error' in res) return res;

      return { success: true, cart_total: res.items.reduce((acc, i) => acc + i.line_total_paise, 0) / 100 };
    } catch (err) {
      return { error: "Could not update cart." };
    }
  });

export const removeVapiFromCart = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    token: z.string(),
    item_id: z.string().uuid(),
    client_request_id: z.string().uuid()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated) return { error: "auth_required" };

      const res = await cartRemoveItem({ data });
      if ('error' in res) return res;

      return { success: true, message: "Item removed.", cart_total: res.items.reduce((acc, i) => acc + i.line_total_paise, 0) / 100 };
    } catch (err) {
      return { error: "Could not remove item." };
    }
  });

export const startVapiCheckout = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ token: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "auth_required" };

      const cart = await cartGet({ data: { token: data.token } });
      if ('error' in cart) return cart;
      if (cart.items.length === 0) return { error: "Your cart is empty." };

      return {
        ready: true,
        message: "Your cart is ready. I'll take you to secure checkout.",
        path: "/checkout"
      };
    } catch (err) {
      return { error: "Could not prepare checkout." };
    }
  });

/**
 * Appointments Tools
 */
export const getVapiConsultationAvailability = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const consultants = await listConsultants();
      if (consultants.length === 0) return { error: "No consultants available." };
      
      const consultant = consultants[0]; // Logic for voice: pick primary
      const res = await getConsultantAvailability({ 
        data: { consultantId: consultant.id } 
      });

      // Filter slots for the requested date (assuming format in blocked/booked)
      // This is a simplification for Stage 3 Voice
      return {
        consultant_name: consultant.name,
        available_slots: ["10:00 AM", "11:30 AM", "02:00 PM", "04:00 PM"], // Mocked for Vapi until ledger parser implemented
        message: `I found several slots available with ${consultant.name} on ${data.date}.`
      };
    } catch (err) {
      return { error: "Could not retrieve availability." };
    }
  });

export const bookVapiAppointment = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    consultant_id: z.string().uuid(),
    type_id: z.string().uuid(),
    start_time: z.string(), // ISO
    date: z.string()
  }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "auth_required" };

      const res = await bookAppointment({ 
        data: { 
          customerId: auth.user.id,
          consultantId: data.consultant_id, 
          consultationTypeId: data.type_id,
          startTime: data.start_time
        } 
      });
      
      return { success: true, message: `Consultation booked for ${data.date}.` };
    } catch (err) {
      return { error: "Could not book appointment." };
    }
  });

/**
 * Order History Tool
 */
export const getVapiMyOrders = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your orders." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: orders } = await supabaseAdmin
        .from("orders")
        .select(`
          id, 
          order_ref, 
          total_amount_paise, 
          status, 
          created_at,
          payment_status,
          meal_plan_title,
          preferred_delivery_date,
          order_items (
            dish_name_snapshot,
            quantity,
            unit_price_paise
          )
        `)
        .eq("customer_id", auth.user.id)
        .order("created_at", { ascending: false })
        .limit(5);

      return { 
        orders: (orders || []).map(o => ({
          id: o.id,
          reference: o.order_ref,
          date: o.created_at,
          status: o.status,
          total: (o.total_amount_paise || 0) / 100,
          plan: o.meal_plan_title,
          delivery_date: o.preferred_delivery_date,
          payment: o.payment_status,
          items: (o.order_items || []).map((i: any) => ({
            name: i.dish_name_snapshot,
            qty: i.quantity,
            price: (i.unit_price_paise || 0) / 100
          }))
        }))
      };
    } catch (err) {
      console.error("[vapi] getVapiMyOrders failed", err);
      return { error: "Could not retrieve your order history." };
    }
  });

export const getVapiOrderDetails = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ order_id: z.string() }).parse(data))
  .handler(async ({ data }) => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: order } = await supabaseAdmin
        .from("orders")
        .select(`
          *,
          order_items (
            dish_name_snapshot,
            quantity,
            unit_price_paise
          )
        `)
        .eq("id", data.order_id)
        .eq("customer_id", auth.user.id)
        .single();

      if (!order) return { error: "Order not found." };

      return { 
        order: {
          reference: order.order_ref,
          status: order.status,
          date: order.created_at,
          total: (order.total_amount_paise || 0) / 100,
          items: (order.order_items || []).map((i: any) => ({
            name: i.dish_name_snapshot,
            qty: i.quantity,
            price: (i.unit_price_paise || 0) / 100
          }))
        }
      };
    } catch (err) {
      return { error: "Could not retrieve order details." };
    }
  });

/**
 * Consultations Tool
 */
export const getVapiMyConsultations = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your consultations." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: consultations } = await (supabaseAdmin as any)
        .from("health_consultations")
        .select("*, consultant:consultants(name), type:consultation_types(name)")
        .eq("customer_id", auth.user.id)
        .order("scheduled_at", { ascending: false });

      return { consultations: consultations as any[] };
    } catch (err) {
      return { error: "Could not retrieve your consultations." };
    }
  });

/**
 * Today's Dishes Tool
 */
export const getVapiTodaysDishes = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    meal_slot: z.string().optional() 
  }).parse(data))
  .handler(async ({ data }) => {
    const traceId = `VAPI_MENU_DATA_TRACE_${Date.now()}`;
    const { todayInIST } = await import("./date/ist");
    const on_date = todayInIST();
    const area_id = "cd329f18-2b21-45d3-8776-8c52ff88a33c"; // Worli
    const meal_slot = data.meal_slot?.toLowerCase() as any;

    console.log(`[${traceId}] getVapiTodaysDishes execution start`, {
      args: data,
      calculated_date: on_date,
      area_id,
      meal_slot
    });

    try {
      const res = await listAlacarte({ 
        data: { 
          area_id, 
          on_date, 
          slot: meal_slot
        } 
      });

      if ('error' in res) {
        console.error(`[${traceId}] listAlacarte error`, res.error);
        return { error: `I couldn't verify the menu for today (Database Error: ${res.error}).` };
      }
      
      const dishes = res.dishes || [];
      console.log(`[${traceId}] listAlacarte success`, {
        rowCount: dishes.length,
        dishes: dishes.map(d => d.name)
      });

      if (dishes.length === 0) {
        return { 
          message: `I couldn't find any dishes available for ${data.meal_slot || 'today'} in Worli.`
        };
      }

      const dishList = dishes.map(d => d.name).join(', ');
      return {
        dishes: dishes.map(d => ({
          name: d.name,
          price: d.unit_price_paise ? d.unit_price_paise / 100 : null,
          description: d.description,
          category: (d as any).category_name || null
        })),
        message: `Today's ${data.meal_slot || 'menu'} in Worli has ${dishes.length} dishes: ${dishList}.`
      };
    } catch (err: any) {
      console.error(`[${traceId}] getVapiTodaysDishes exception`, err);
      return { error: `I couldn't verify the menu for today (Exception: ${err.message}).` };
    }
  });

/**
 * Subscription Tool
 */
export const getVapiMySubscription = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your subscription." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: subscription } = await supabaseAdmin
        .from("mp2_subscriptions")
        .select("*, plan:mp2_plans(title)")
        .eq("customer_id", auth.user.id)
        .eq("status", "active")
        .maybeSingle();

      if (!subscription) return { message: "You don't have an active meal plan subscription." };

      return { subscription };
    } catch (err) {
      return { error: "Could not retrieve your subscription details." };
    }
  });

/**
 * Wallet Tool
 */
export const getVapiMyWallet = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your wallet balance." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: wallet } = await supabaseAdmin
        .from("customer_wallets")
        .select("balance_paise")
        .eq("customer_id", auth.user.id)
        .maybeSingle();

      return { balance: (wallet?.balance_paise || 0) / 100 };
    } catch (err) {
      return { error: "Could not retrieve your wallet balance." };
    }
  });

/**
 * Delivery Status Tool
 */
export const getVapiDeliveryStatus = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const { listCustomerSubscriptions, getCustomerSubscription } = await import("./customer/subscriptions-api");
      const subs = await listCustomerSubscriptions({ limit: 1, offset: 0 });

      if (subs.rows.length === 0) return { message: "No active subscription found." };

      const detail = await getCustomerSubscription(subs.rows[0].id);
      
      const { todayInIST } = await import("./date/ist");
      const today = todayInIST();
      
      const next = detail.ledger.find((l: any) => l.delivery_date >= today && l.status === "scheduled");
      const last = [...detail.ledger].reverse().find((l: any) => l.delivery_date <= today && l.status !== "scheduled");

      return {
        next_delivery: next ? { date: next.delivery_date, status: "scheduled" } : null,
        last_delivery: last ? { date: last.delivery_date, status: last.status } : null,
        plan_name: detail.plan_name
      };
    } catch (err) {
      console.error("[vapi] getVapiDeliveryStatus failed", err);
      return { error: "I couldn't verify your delivery status right now." };
    }
  });

/**
 * Remaining Meals Tool
 */
export const getVapiMyMeals = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your remaining meals." };
      
      const { listCustomerSubscriptions } = await import("./customer/subscriptions-api");
      const subs = await listCustomerSubscriptions({ limit: 1, offset: 0 });
      
      if (subs.rows.length === 0) return { message: "No active meal plan subscription found." };
      
      return { remaining_meals: subs.rows[0].remaining_meals };
    } catch (err) {
      console.error("[vapi] getVapiMyMeals failed", err);
      return { error: "I couldn't verify your remaining meals right now." };
    }
  });

/**
 * Delivery Address Tool
 */
export const getVapiMyDeliveryAddress = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const auth = await getVapiAuthStatus();
      if (!auth.authenticated || !auth.user) return { error: "Please log in to view your delivery address." };
      
      const { listCustomerAddresses } = await import("./customer/customer-api");
      const addresses = await listCustomerAddresses();
      const defaultAddr = addresses.find(a => a.is_default) || addresses[0];
      
      if (!defaultAddr) return { message: "No delivery address found." };
      
      return {
        address: `${defaultAddr.address_line1}, ${defaultAddr.address_line2 ? defaultAddr.address_line2 + ', ' : ''}${defaultAddr.delivery_area_name || ''}, ${defaultAddr.city || ''}, ${defaultAddr.pincode || ''}`
      };
    } catch (err) {
      console.error("[vapi] getVapiMyDeliveryAddress failed", err);
      return { error: "I couldn't verify your delivery address right now." };
    }
  });
